import {
  AlignItems,
  Box,
  Color,
  Display,
  Display1,
  FlexDirection,
  FontFamily,
  FontWeight,
  Image,
  JustifyContent,
  Space,
  Standfirst1,
  Text,
  TextAlign,
  useTheme,
  Visible,
} from '@gousto-internal/citrus-react'
import React, { useCallback, useEffect, useRef, useState } from 'react'

import { canUseWindow } from '@library/environment'
import { queueTrackingEvent } from '@library/tracking-integration'
import { debounce } from 'lodash'
import { RafLandingPageType } from '../../Pages/RafLandingPage'
import { RafCampaignDetails } from '../../hooks/useFetchRafCampaignDetails'
import { RafTrackingEventType, StickyTrackingEvent } from '../../tracking/trackingConfig'
import { HERO_CONFIG, IMAGES } from './Hero.config'
import { CTABlock, FriendGiftText } from './components'
import {
  GetStartedFloatingCTA,
  GetStartedProps,
} from './components/GetStartedFloatingCTA/GetStartedFloatingCTA'
import { DiscountBoxesThreeTier } from './components/DiscountBoxesThreeTier'
import { DiscountBoxesThreeTierMobile } from './components/DiscountBoxesThreeTierMobile'

type HeroProps = {
  isRafLandingPageExperimentEnabled: boolean
}

export const ThreeStepHero = ({
  firstBoxDiscount,
  firstMonthDiscount,
  secondBoxDiscount,
  giftYourFriendCredit,
  onScrollHandler,
  onCTAClick,
  signupCTA,
  isRafLandingPageExperimentEnabled,
}: HeroProps &
  GetStartedProps &
  RafCampaignDetails &
  Pick<RafLandingPageType, 'onScrollHandler'>) => {
  const [isSticky, setIsSticky] = useState(false)
  const [_maxHeight, setMaxHeight] = useState(0)
  const { theme } = useTheme()
  const breakpointToMedium = Number(theme.tokens.breakpoints[0].replace('px', ''))

  const heroRef = useRef<HTMLDivElement>(null)
  const stickyCTARef = useRef<HTMLDivElement>(null)
  const [isHeroInView, setIsHeroInView] = useState(true)

  const onScroll = useCallback(() => {
    onScrollHandler({ heroRef, stickyCTARef, setIsSticky, setMaxHeight })
  }, [onScrollHandler])

  const debounceScroll = debounce(onScroll, 200)

  useEffect(() => {
    if (canUseWindow()) {
      window?.addEventListener('scroll', debounceScroll)
    }

    if (window?.innerWidth < breakpointToMedium) {
      queueTrackingEvent<RafTrackingEventType>(
        StickyTrackingEvent({ isVisible: isSticky, name: 'sticky_footer_cta_with_discount' }),
      )
    }

    return () => {
      if (canUseWindow()) {
        window.removeEventListener('scroll', debounceScroll)
      }
    }
  }, [breakpointToMedium, debounceScroll, isSticky])

  useEffect(() => {
    if (!canUseWindow) return

    const observer = new IntersectionObserver(([entry]) => setIsHeroInView(entry.isIntersecting), {
      root: null,
      rootMargin: '0px',
      threshold: 0,
    })

    if (heroRef.current) {
      observer.observe(heroRef.current)
    }

    return () => {
      observer.disconnect()
    }
  }, [])

  return (
    <Box
      paddingH={[3, 8]}
      paddingV={[0, 0, 0, 14]}
      display={Display.Flex}
      flexDirection={FlexDirection.Column}
      bg={Color.ColdGrey_50}
      justifyContent={JustifyContent.Center}
      alignItems={AlignItems.Center}
      ref={heroRef}
    >
      <Box
        display={Display.Flex}
        alignItems={[AlignItems.Center, null, AlignItems.Center]}
        justifyContent={JustifyContent.Center}
        flexDirection={[
          FlexDirection.Column,
          FlexDirection.Column,
          FlexDirection.Column,
          FlexDirection.Row,
        ]}
        width="100%"
        maxWidth="1400px"
        className="main"
        paddingH={[null, null, null, null]}
        gap={[2, 0, 3]}
      >
        <Visible display={[Display.Block, Display.Block, Display.Block, Display.None]}>
          <Box maxWidth="360px" justifyContent={JustifyContent.Center}>
            <Image
              fit="contain"
              srcSet={`${IMAGES.mobile.webp.x1} 1x, ${IMAGES.mobile.webp.x2} 2x`}
              src={IMAGES.mobile.png}
              alt=""
            />
          </Box>
        </Visible>
        <Box
          justifyContent={[
            JustifyContent.Center,
            JustifyContent.Center,
            JustifyContent.Center,
            JustifyContent.FlexStart,
          ]}
          alignItems={AlignItems.FlexStart}
          display={Display.Flex}
          width={['100%', null, null, '30%']}
        >
          <Box>
            <Display1
              textAlign={[TextAlign.Center, TextAlign.Center, TextAlign.Center, TextAlign.Left]}
              size={[13, null, 14]}
            >
              {HERO_CONFIG.heading}
            </Display1>
            <Box
              justifyContent={[
                JustifyContent.Center,
                JustifyContent.Center,
                JustifyContent.Center,
                JustifyContent.FlexStart,
              ]}
              display={Display.Flex}
              width="100%"
            >
              <Box
                paddingV={[1, 3]}
                display={Display.Flex}
                flexDirection={FlexDirection.Column}
                maxWidth={[null, '95%', '100%', '85%']}
                justifyContent={JustifyContent.Center}
              >
                <Standfirst1
                  color={Color.ColdGrey_800}
                  size={[5, null, 8]}
                  textAlign={[TextAlign.Center, TextAlign.Center, TextAlign.Center, TextAlign.Left]}
                >
                  Over{' '}
                  <Text
                    size={[5, null, 8]}
                    color={Color.ColdGrey_800}
                    display={Display.Inline}
                    fontFamily={FontFamily.Bold}
                  >
                    a million{' '}
                  </Text>
                  people have signed up for Gousto thanks to their mates
                </Standfirst1>
              </Box>
            </Box>
            <Visible display={[Display.None, Display.None, Display.None, Display.Block]}>
              <Box id="friendGiftSection" paddingV={[0, 1]} paddingBottom={[0, 5]}>
                <FriendGiftText giftYourFriendCredit={giftYourFriendCredit} />
              </Box>
            </Visible>
            <Visible display={[Display.None, Display.None, Display.None, Display.Block]}>
              <CTABlock />
            </Visible>
          </Box>
        </Box>
        <Space direction="horizontal" size={4} />
        <Box display={Display.Flex} gap={[2, null, 8]} justifyContent={JustifyContent.Center}>
          <Visible display={[Display.None, Display.None, Display.None, Display.Block]}>
            <DiscountBoxesThreeTier
              firstBoxDiscount={firstBoxDiscount}
              firstMonthDiscount={firstMonthDiscount}
              secondBoxDiscount={secondBoxDiscount}
            />
          </Visible>
          <Box
            display={[Display.Flex, Display.Flex, Display.Flex, Display.None]}
            width={'100%'}
            justifyContent={JustifyContent.Center}
            flexDirection={FlexDirection.Column}
            alignItems={AlignItems.Center}
            gap={2}
          >
            <Text color={Color.ColdGrey_800} size={8} fontWeight={FontWeight.Bold}>
              You get
            </Text>
            <DiscountBoxesThreeTierMobile
              firstBoxDiscount={firstBoxDiscount}
              firstMonthDiscount={firstMonthDiscount}
              secondBoxDiscount={secondBoxDiscount}
            />
          </Box>
          <Visible display={[Display.None, Display.None, Display.None, Display.Block]}>
            <Box
              width={'30vw'}
              maxWidth={'468px'}
              display={Display.Flex}
              justifyContent={JustifyContent.Center}
              alignItems={AlignItems.Center}
            >
              <Image
                src={IMAGES.desktopRafStepped.png}
                alt=""
                fit="cover"
                style={{ width: '468px', height: '468px', objectFit: 'cover' }}
              />
            </Box>
          </Visible>
        </Box>
      </Box>
      <Box
        display={Display.Flex}
        flexDirection={FlexDirection.Column}
        justifyContent={JustifyContent.Center}
        alignItems={AlignItems.Center}
        width="100%"
      >
        <Box
          display={[Display.Flex, Display.Flex, Display.Flex, Display.None]}
          flexDirection={FlexDirection.Column}
          alignItems={AlignItems.Center}
        >
          <Space direction="vertical" size={4} />
          <FriendGiftText giftYourFriendCredit={giftYourFriendCredit} />
          <Space direction="vertical" size={4} />
          <CTABlock justifyBenefitText={JustifyContent.Center} />
          <Space direction="vertical" size={6} />
        </Box>
      </Box>
      {isRafLandingPageExperimentEnabled && !isHeroInView && (
        <GetStartedFloatingCTA signupCTA={signupCTA} onCTAClick={onCTAClick} />
      )}
    </Box>
  )
}
